/**
 * Analyzer: Progress Bar Patterns
 *
 * Cross-day patterns that emerge from `trade.barAnalysis` once enough trades
 * have it populated (the recently-shipped backfill is the enabling step —
 * before that, only manually-opened trades had bar context).
 *
 * Different from the day-scope `dayBarContext` analyzer in two ways:
 *
 *   1. Looks at the LAST N days of trades, not just one day. That lets
 *      patterns surface that a single day can't reveal — "your sharp
 *      entries cluster between 9:30 and 10:00 ET" needs many days of data.
 *
 *   2. Emits `scope: 'progress'` observations, which surface in the
 *      Live Coach Feed's "30D TRENDS" section instead of the per-day list.
 *      Different real estate, different framing.
 *
 * Patterns this round:
 *
 *   - Hour-of-day sharp-entry clustering. Finds the hour where the trader's
 *     "sharp entries" (low MAE, meaningful MFE) are most concentrated. If
 *     one hour holds a meaningful share of all sharp entries (and a
 *     meaningful absolute count), emit positive reinforcement naming the
 *     hour. Helps the trader recognize WHEN their edge fires.
 *
 *   - Hour-of-day MFE-leakage clustering. Inverse pattern: hour with the
 *     highest density of low-capture winners. "Your worst MFE leakage
 *     happens 14:00-15:00 ET" — actionable for late-day exit habits.
 *
 * Both gate on a minimum sample size + a meaningful concentration over the
 * baseline rate so we don't fire on coincidences.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { getTradingDay, addDaysToDateStr } from '../../../timeService.js';

export const key = 'progressBarPatterns';
export const tier = 'free';

// ── Thresholds ─────────────────────────────────────────────────────────────
// Conservative defaults — better to undershoot than to flood the feed with
// weak signals. Tighten or loosen against real data after Phase D-7 backfill.

// Sharp entry definition (must match dayBarContext for consistency)
const SHARP_MAX_MAE_R    = 0.25;
const SHARP_MIN_MFE_R    = 1.5;

// MFE leakage definition: winner that captured <30% of available MFE
const LEAKAGE_MAX_CAPTURE = 0.30;

// Minimum absolute count of pattern hits before we'll emit anything
const MIN_PATTERN_COUNT  = 4;

// The "winning" hour must hold at least this share of all hits in its bucket
const MIN_HOUR_SHARE     = 0.35;
// AND must be at least this multiple of the average per-hour rate (to avoid
// "9 hours x 1 hit each + one hour with 2 hits")
const MIN_HOUR_X_AVG     = 2.0;

// Trade must have barAnalysis with R values to count
const isUsable = (trade) => {
  const a = trade?.barAnalysis;
  if (!a || typeof a !== 'object') return false;
  if (!Number.isFinite(a.maeR) || !Number.isFinite(a.mfeR)) return false;
  return true;
};

// ── Hour-of-day extraction ─────────────────────────────────────────────────

const hourOfEntryTime = (entryTime) => {
  if (!entryTime || typeof entryTime !== 'string') return null;
  const m = entryTime.match(/^(\d{1,2}):/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  return Number.isFinite(h) && h >= 0 && h <= 23 ? h : null;
};

const fmtHour = (h) => {
  if (!Number.isFinite(h)) return '';
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
};

const fmtHourRange = (h) => `${fmtHour(h)}–${fmtHour((h + 1) % 24)}`;

const fmtPct = (n) => `${Math.round(n * 100)}%`;

// ── Window filter ──────────────────────────────────────────────────────────
//
// Uses the project-wide trading-day boundary via getTradingDay. The lookback
// window is computed in trading-day strings, so a trade entered at 7pm ET
// today and a trade at 9am ET today both count as "today" — same way the
// rest of the journal classifies them.

const inWindow = (trade, fromDateStr, toDateStr) => {
  if (!trade?.date || typeof trade.date !== 'string') return false;
  return trade.date >= fromDateStr && trade.date <= toDateStr;
};

// ── Hour-bucket aggregator ─────────────────────────────────────────────────
//
// Walks `trades`, applies `predicate`, and counts hits per hour bucket.
// Returns: { byHour: { 9: 4, 10: 6, ... }, total: number, totalUsable: number }

const bucketByHour = (trades, predicate) => {
  const byHour = {};
  let total = 0;
  let totalUsable = 0;
  for (const t of trades) {
    if (!isUsable(t)) continue;
    totalUsable++;
    if (!predicate(t)) continue;
    const h = hourOfEntryTime(t.entryTime);
    if (h === null) continue;
    byHour[h] = (byHour[h] || 0) + 1;
    total++;
  }
  return { byHour, total, totalUsable };
};

// Pick the dominant hour from a bucket, gated by:
//   - absolute count >= MIN_PATTERN_COUNT
//   - share of total >= MIN_HOUR_SHARE
//   - count >= MIN_HOUR_X_AVG * (total / numHoursWithHits)
// Returns { hour, count, share } or null if no hour qualifies.
const dominantHour = (byHour, total) => {
  if (total < MIN_PATTERN_COUNT) return null;
  const entries = Object.entries(byHour).map(([h, c]) => ({ hour: parseInt(h, 10), count: c }));
  if (entries.length === 0) return null;
  entries.sort((a, b) => b.count - a.count);
  const top = entries[0];
  const share = top.count / total;
  const avg = total / entries.length;
  if (share < MIN_HOUR_SHARE) return null;
  if (top.count < avg * MIN_HOUR_X_AVG) return null;
  return { hour: top.hour, count: top.count, share };
};

// ── Pattern 1: sharp-entry hour clustering ────────────────────────────────

const detectSharpHourCluster = ({ trades, windowDays }) => {
  const isSharp = (t) => {
    const a = t.barAnalysis;
    return a.maeR <= SHARP_MAX_MAE_R && a.mfeR >= SHARP_MIN_MFE_R;
  };
  const { byHour, total } = bucketByHour(trades, isSharp);
  const top = dominantHour(byHour, total);
  if (!top) return null;

  return makeObservation({
    id: `${key}:sharp-hour:${top.hour}`,
    analyzer: key,
    scope: 'progress',
    scopeId: `${windowDays}d`,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.EXECUTION,
    title: `Your sharp entries cluster ${fmtHourRange(top.hour)} ET`,
    detail: `${top.count} of ${total} sharp entries over the last ${windowDays} days (${fmtPct(top.share)}) landed in this hour. That's where your edge concentrates.`,
    action: `Treat ${fmtHourRange(top.hour)} as a "lean in" window — and treat the rest as "be choosier." Note what specifically primes you to see clean entries here.`,
    evidence: {
      hour: top.hour,
      count: top.count,
      share: top.share,
      total,
      windowDays
    },
    confidence: 0.8,
    priority: 65
  });
};

// ── Pattern 2: MFE-leakage hour clustering ────────────────────────────────

const detectLeakageHourCluster = ({ trades, windowDays }) => {
  const isLeaky = (t) => {
    if (!Number.isFinite(t.netPnl) || t.netPnl <= 0) return false;       // winners only
    const mfeD = t.barAnalysis.mfeDollars;
    if (!Number.isFinite(mfeD) || mfeD <= 0) return false;
    const capture = t.netPnl / mfeD;
    return capture < LEAKAGE_MAX_CAPTURE;
  };
  const { byHour, total } = bucketByHour(trades, isLeaky);
  const top = dominantHour(byHour, total);
  if (!top) return null;

  return makeObservation({
    id: `${key}:leakage-hour:${top.hour}`,
    analyzer: key,
    scope: 'progress',
    scopeId: `${windowDays}d`,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `MFE leakage clusters ${fmtHourRange(top.hour)} ET`,
    detail: `${top.count} of ${total} winners with <${fmtPct(LEAKAGE_MAX_CAPTURE)} capture over the last ${windowDays} days fell in this hour (${fmtPct(top.share)}). You exit early most often here.`,
    action: `Set a "let it run" rule for entries during ${fmtHourRange(top.hour)} — partial at usual target, runner with trailing stop.`,
    evidence: {
      hour: top.hour,
      count: top.count,
      share: top.share,
      total,
      windowDays
    },
    confidence: 0.78,
    priority: 60
  });
};

// ── Public analyze() ──────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { allTrades, currentTradingDay, windowDays = 30 } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;

  const anchor = currentTradingDay || getTradingDay(Date.now()) || null;
  if (!anchor) return null;
  const fromDateStr = addDaysToDateStr(anchor, -windowDays);

  const windowed = allTrades.filter(t => inWindow(t, fromDateStr, anchor));
  if (windowed.length === 0) return null;

  const obs = [];
  const sharp = detectSharpHourCluster({ trades: windowed, windowDays });
  if (sharp) obs.push(sharp);
  const leakage = detectLeakageHourCluster({ trades: windowed, windowDays });
  if (leakage) obs.push(leakage);

  return obs.length === 0 ? null : obs;
};

// Exports for tests
export {
  hourOfEntryTime,
  bucketByHour,
  dominantHour,
  detectSharpHourCluster,
  detectLeakageHourCluster
};
